import React, { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import {
  Container,
  Grid,
  Box,
  Alert,
  Paper,
  Anchor,
  Group,
  Badge,
  LoadingOverlay,
  Stack,
} from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { useMantineColorScheme } from "@mantine/core";

import { Routes as ReactRouterRoutes, Route } from "react-router";

import { useNavigateParams, useRequest } from "./hooks";
import { debugLog } from "./utils/debug";
import { IconDownload } from "@tabler/icons-react";
import { Customer, Banner } from "./types";

const Navigation = React.lazy(() => import("./components/navigation/Navigation"));
const Timepicker = React.lazy(() => import("./components/timepicker/Timepicker"));
const DataTableWrapper = React.lazy(() => import("./components/table/DataTableWrapper"));
const ChartWrapper = React.lazy(() => import("./components/chart/ChartWrapper"));
const Debug = React.lazy(() => import("./components/Debug"));
const Support = React.lazy(() => import("./components/Support"));

export default function Routes(): React.JSX.Element {
  const { setQueryNavigation, getURLQueryParams } = useNavigateParams();
  const { data: banners, load: loadBanners } = useRequest<Banner[]>();
  const { data: customers, load: loadCustomers } = useRequest<Customer[]>();

  // Derive customersData from customers to avoid synchronous setState in effect
  const customersData = useMemo<Customer[]>(() => {
    return customers || [];
  }, [customers]);

  // Load customers and banners on mount
  useEffect(() => {
    loadBanners("/api/banners");
    loadCustomers("/api/menu");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // load functions are stable from useRequest hook

  // Set default navigation if no URL params
  useEffect(() => {
    if (customersData?.length && !getURLQueryParams().customerId) {
      const firstCustomer = customersData[0];
      const firstReport = firstCustomer?.reports?.[0];

      if (firstCustomer && firstReport) {
        const defaultQuery: Record<string, string> = {
          startDate: dayjs().utc().startOf("day").valueOf().toString(),
          endDate: dayjs().utc().endOf("day").valueOf().toString(),
          customerId: firstCustomer.id,
          reportId: firstReport.id,
        };

        if (firstReport.groups?.[0]) {
          defaultQuery["group"] = firstReport.groups[0];
        }

        setQueryNavigation("/table", defaultQuery);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersData]); // getURLQueryParams and setQueryNavigation are stable from hook

  // Use Mantine v8 color scheme hook
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  useHotkeys([["mod+J", () => toggleColorScheme()]]);

  debugLog("*** Routes() ***", {
    colorScheme,
    banners,
  });

  if (!customersData?.length) return <LoadingOverlay visible />;

  return (
    <Container fluid>
      <Grid>
        <Grid.Col span={12}>
          <Stack gap="xs">
            {banners &&
              banners.map(
                (banner, index) =>
                  banner.enabled && (
                    <Alert
                      key={index}
                      title={banner.title}
                      variant="filled"
                      color={banner.type === "info" ? "telia" : "red"}
                    >
                      {banner.message}
                    </Alert>
                  )
              )}
          </Stack>
        </Grid.Col>
        <Grid.Col style={{ minWidth: 350, maxWidth: 350 }}>
          <Navigation />
          <Timepicker />
          <Paper withBorder p="xs" mt="xs" shadow="sm">
            <Group justify="space-between">
              <Anchor href="/Trafikstatistik_Userguide_1.5.pdf" size="xs" target="_blank">
                <Group>
                  <IconDownload size={14} /> Download Userguide (v1.5)
                </Group>
              </Anchor>
              <Badge size="sm">2023-10-18</Badge>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col
          span={{ xs: 12, md: 10, lg: 10, xl: 10 }}
          style={{ minWidth: "300px", maxWidth: "calc(100vw - 400px)" }}
        >
          <Box pos="relative">
            <ReactRouterRoutes>
              <Route path="/table" element={<DataTableWrapper />} />
              <Route path="/chart" element={<ChartWrapper />} />
              <Route path="/debug" element={<Debug />} />
              <Route path="/support" element={<Support />} />
            </ReactRouterRoutes>
          </Box>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
